import { forwardRef, PropsWithChildren } from 'react';
import { observer } from 'mobx-react-lite';
import { IStickyHeaderProps } from './sticky-header.d';
import { StickyContainer, StickyInner } from './sticky-header.styled';
import { DS_COLORS } from 'styles';

/**
 * StickyHeader — универсальный компонент для создания "прилипающего" заголовка с двумя уровнями оформления.
 *
 * ### Почему нужна двойная вложенность:
 * - Внешний контейнер (`StickyContainer`) необходим для создания общего фона,
 *   который остаётся видимым за блоком внутреннего содержимого (`StickyInner`) при скролле.
 *   Это важно, чтобы заголовок сохранял визуальную связь с соседними элементами страницы.
 * - Внутренний контейнер (`StickyInner`) добавляет скруглённые углы и отвечает за фон самого заголовка.
 *   При скролле внутренний блок остаётся скруглённым, создавая аккуратный визуальный эффект,
 *   который сложно достичь при использовании только одного уровня вложенности.
 *
 * ### Пример проблемы:
 * Если использовать только один контейнер, то скруглённые углы заголовка пропадают,
 * когда его верхняя часть "прилипает" к границе экрана. Двойная вложенность позволяет
 * разделить эти эффекты: внешний фон остаётся неизменным, а внутренний блок сохраняет свои углы.
 *
 * @param {IProps} props - Свойства компонента.
 * @returns {JSX.Element} JSX-элемент.
 *
 * @example
 * <StickyHeader
 *   outerBgColor="DS_COLORS.gray[50]"
 *   innerBgColor="DS_COLORS.white"
 *   round={12}
 *   top={0}
 * >
 *   <h4>Заголовок какой-то</h4>
 * </StickyHeader>
 */
export const StickyHeader = observer(
  forwardRef<HTMLDivElement, PropsWithChildren<IStickyHeaderProps>>(
    (
      {
        outerBgColor = DS_COLORS.gray[50],
        innerBgColor = DS_COLORS.white,
        round = 0,
        top = 0,
        children,
      },
      ref
    ) => {
      return (
        <StickyContainer ref={ref} top={top} bgColor={outerBgColor}>
          <StickyInner bgColor={innerBgColor} round={round}>
            {children}
          </StickyInner>
        </StickyContainer>
      );
    }
  )
);
